import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { RootState } from "../../../globals";
import HttpManager from "../../../services/HttpManager";
import { ProspectCatalogs } from "../../../services/v2/prospects.service";
import SalesController from "./salesController";
import CrmLista, { ListaNav } from "./crmLista";
import CrmFicha, { FichaNav } from "./crmFicha";
import NewProspectModal from "./newProspectModal";
import MarcadorView from "./marcadorView";
import CarteraView from "./carteraView";
import MisVentasView from "./misVentasView";
import CampanasView from "./campanasView";
import NumerosModal from "./numerosModal";
import VentasBarra from "./ventasBarra";
import { marcador } from "./marcador";
import { TelefonoPanel } from "./telefono/telefonoUI";
import { errorTexto } from "./salesHelpers";
import { useAltoVisible } from "./altoVisible";
import "./sales.css";
import "./crm.css";

/**
 * Ventas en dos pestañas (Esteban, 2026-10-02):
 *  - CRM: la vista tipo Zoho, permanente. Lista con filtros guardados y ficha por secciones.
 *  - WappChat (antes "Mensajería"): la bandeja todo en uno (chat de WhatsApp y teléfono dentro de la ficha). Para las asesoras se enciende
 *    cuando dejen Woztell y VozMia; Esteban y Supervisión ya la ven.
 * Las dos abren a la misma persona: "Ver en WappChat" / "Ver en CRM". La URL guarda dónde estás (?vista=msj&id=123).
 */
const CrmController: React.FC = () => {
	const http = HttpManager.getInstance();
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	const campusId: number | null = legacyCampus?.id ?? sessionCampus?.id ?? null;

	const navigate = useNavigate();
	// WappChat tiene su propia sección (/wappchat) cuando el rol la trae (sección 72); si no, sigue aquí como pestaña.
	const navegacion: any[] | null = useSelector((state: RootState) => state.navigation.value);
	const wappchatAparte = !!navegacion?.some((n: any) => n.path === "wappchat");
	const [params, setParams] = useSearchParams();
	const vista = params.get("vista") === "msj" ? "msj" : "crm";
	const sel = Number(params.get("id")) || null;
	const [catalogs, setCatalogs] = useState<ProspectCatalogs | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [nuevo, setNuevo] = useState<boolean>(false);
	const [refresco, setRefresco] = useState<number>(0);
	const [volver, setVolver] = useState<string>("Mi cartera");
	const [lista, setLista] = useState<ListaNav | null>(null);
	// Herramientas de supervisión (Esteban, 2026-10-05: "¿por qué Cartera si ya tenemos CRM?"): vivían en WappChat; son del CRM.
	const [herr, setHerr] = useState<"marcador" | "cartera" | "campanas" | "misventas" | null>(null);
	const [numeros, setNumeros] = useState<boolean>(false);
	const [ownerHerr, setOwnerHerr] = useState<string>("all");

	useEffect(() => {
		if (!campusId) return;
		http.prospectsService.catalogs(campusId).then((r) => setCatalogs(r.data)).catch((e) => setError(errorTexto(e)));
	}, [campusId]);

	// /prospects?marcador=1 (aviso de la mañana): abre el CRM con el panel del marcador abierto.
	useEffect(() => {
		if (params.get("marcador") !== "1") return;
		marcador.ponerAbierto(true);
		const next: { [k: string]: string } = {};
		params.forEach((v, k) => { if (k !== "marcador") next[k] = v; });
		setParams(next, { replace: true });
	}, [params]);

	// /prospects?herr=cartera|tablero|campanas|marcador|numeros: Herramientas elegidas desde WappChat o WappTeam.
	useEffect(() => {
		const h = params.get("herr");
		if (!h) return;
		if (h === "numeros") setNumeros(true);
		// El Tablero ya es sección propia (2026-11-04): los enlaces viejos (?herr=tablero, WappTeam) van allá.
		else if (h === "tablero") navigate("/tableroVentas", { replace: true });
		else if (h === "cartera" || h === "campanas" || h === "marcador" || h === "misventas") setHerr(h);
		const next: { [k: string]: string } = {};
		params.forEach((v, k) => { if (k !== "herr") next[k] = v; });
		setParams(next, { replace: true });
	}, [params]);

	const ver = (v: "crm" | "msj", id: number | null) => {
		if (v === "msj" && wappchatAparte) {
			navigate("/wappchat" + (id ? "?id=" + id : ""));
			return;
		}
		setHerr(null);
		const next: { [k: string]: string } = {};
		if (v === "msj") next.vista = "msj";
		if (id) next.id = String(id);
		setParams(next);
	};
	// WappChat: quien tenga la sección (desde el 10-oct también las asesoras, con "Mis clientes"). Herramientas
	// (auto-marcador, cartera, tablero, campañas, rotación) sigue solo para Supervisión.
	const esSup = !!catalogs?.isSupervisor;
	// El marcador abre a la persona en la vista donde ya estaba (CRM o WappChat).
	const verRef = useRef(ver);
	verRef.current = ver;
	const vistaRef = useRef(vista);
	vistaRef.current = vista;
	const abrirDesdeMarcador = useCallback((id: number) => verRef.current(vistaRef.current === "msj" ? "msj" : "crm", id), []);
	const veWappchat = esSup || wappchatAparte;
	const veWappteam = !!navegacion?.some((n: any) => n.path === "wappteam");

	// Anterior/siguiente en la ficha (Esteban, 2026-10-12, como Zoho): el orden es el de la lista de la que vino; en la
	// orilla de la página pide la página de al lado y abre al primero (o al último).
	// La persona guarda su lugar (Esteban, 2026-10-20: "no permite pasar al siguiente contacto"): al guardar una nota la
	// lista se recarga y la persona sale del filtro o sube por último contacto; los vecinos se miden contra ese lugar.
	const lugar = useRef<{ id: number; i: number; page: number } | null>(null);
	const nav: FichaNav | undefined = (() => {
		if (!sel || !lista) { lugar.current = null; return undefined; }
		const visto = lista.ids.indexOf(sel);
		const l0 = lugar.current;
		if (visto >= 0 && (!l0 || l0.id !== sel || l0.page !== lista.page)) lugar.current = { id: sel, i: visto, page: lista.page };
		const l = lugar.current;
		if (!l || l.id !== sel || l.page !== lista.page) return undefined;
		const resto = lista.ids.filter((x) => x !== sel);
		const i = Math.min(l.i, resto.length);
		const pos = (lista.page - 1) * lista.perPage + i + 1;
		const prev = i > 0 ? () => ver("crm", resto[i - 1])
			: lista.page > 1 ? () => { lista.ir(lista.page - 1).then((ids) => { if (ids.length) ver("crm", ids[ids.length - 1]); }); } : null;
		const next = i < resto.length ? () => ver("crm", resto[i])
			: lista.page * lista.perPage < lista.total ? () => { lista.ir(lista.page + 1).then((ids) => { if (ids.length) ver("crm", ids[0]); }); } : null;
		return { pos, total: Math.max(lista.total, pos), prev, next };
	})();

	const raiz = useRef<HTMLDivElement>(null);
	useAltoVisible(raiz, campusId);

	// Enlaces viejos (prospects?vista=msj&id=N) siguen sirviendo: llevan a la misma persona en /wappchat.
	if (vista === "msj" && wappchatAparte) return <Navigate to={"/wappchat" + (sel ? "?id=" + sel : "")} replace />;
	if (!campusId) return <div className="crm"><div className="ventas-vacio">Elige un plantel en la barra de arriba.</div></div>;

	return (
		<div className="crm" ref={raiz}>
			{/* La barra va para todas (Esteban, 2026-10-09: las asesoras no veían el marcador, "that's a little stupid"). */}
			<VentasBarra activo={vista === "msj" ? "msj" : "crm"} campusId={campusId} veCrm veWappchat={veWappchat} veWappteam={veWappteam}
				onCrm={() => ver("crm", sel)} onWappchat={() => ver("msj", sel)} onWappteam={() => navigate("/wappteam")}
				onAbrirMarcador={abrirDesdeMarcador} catalogs={catalogs} herr={herr} onHerr={setHerr} onNumeros={() => setNumeros(true)}
				onCampanas={() => setHerr("marcador")} />
			{error ? <div className="crm-aviso error" style={{ margin: 12 }}>{error}</div> : null}
			<div className="crm-cuerpo">
				{herr && (esSup || herr === "marcador" || herr === "misventas") ? (
					<div className="ventas">
						{herr === "marcador" ? (
							<MarcadorView campusId={campusId} catalogs={catalogs} onSalir={() => setHerr(null)} />
						) : herr === "cartera" ? <CarteraView campusId={campusId} catalogs={catalogs} owner={ownerHerr} onOwner={setOwnerHerr} onSalir={() => setHerr(null)} onOpen={(id) => ver("crm", id)} />
						: herr === "misventas" ? <MisVentasView campusId={campusId} onSalir={() => setHerr(null)} />
						: <CampanasView campusId={campusId} catalogs={catalogs} onSalir={() => setHerr(null)} />}
					</div>
				) : vista === "msj" && veWappchat ? (
					<SalesController key={"msj" + (sel || "")} inicial={sel} onVerCrm={(id) => ver("crm", id)} />
				) : (
					<>
						{/* La lista queda montada debajo de la ficha: al volver conserva filtro, búsqueda y página. */}
						<div className={sel ? "crm-oculto" : ""} style={{ display: "flex", flex: 1, minWidth: 0 }}>
							<CrmLista campusId={campusId} catalogs={catalogs} refresco={refresco} onNuevo={() => setNuevo(true)}
								onOpen={(id, titulo) => { setVolver(titulo); ver("crm", id); }} onChat={veWappchat ? (id) => ver("msj", id) : undefined} onNav={setLista} />
						</div>
						{sel ? (
							<CrmFicha campusId={campusId} prospectId={sel} catalogs={catalogs} volverTexto={volver}
								onBack={() => ver("crm", null)} onOpen={(id) => ver("crm", id)} onChanged={() => setRefresco((n) => n + 1)}
								onWappchat={veWappchat ? (id) => ver("msj", id) : undefined} nav={nav} />
						) : null}
					</>
				)}
			</div>
			<TelefonoPanel campusId={campusId} onAbrir={(id) => ver("crm", id)} />
			{numeros ? <NumerosModal campusId={campusId} catalogs={catalogs} onClose={() => setNumeros(false)} /> : null}
			{nuevo ? (
				<NewProspectModal campusId={campusId} catalogs={catalogs} onClose={() => setNuevo(false)}
					onCreated={(id) => { setNuevo(false); setRefresco((n) => n + 1); ver("crm", id); }}
					onOpenExisting={(id) => { setNuevo(false); ver("crm", id); }} />
			) : null}
		</div>
	);
};

export default CrmController;
